"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.__testing = exports.PENDING_CONTENTS_VIEW_NAME = exports.ContentsViewNameSentinel = void 0;
exports.materializeOrCadDsnCfbDocument = materializeOrCadDsnCfbDocument;
const OrcadDsnCfbStreamUtils_1 = require("./OrcadDsnCfbStreamUtils");
const OrcadDsnCfbTypes_1 = require("./OrcadDsnCfbTypes");
const OrcadDsnCfbOccurrenceOwnership_1 = require("./OrcadDsnCfbOccurrenceOwnership");
var ContentsViewNameSentinel;
(function (ContentsViewNameSentinel) {
    ContentsViewNameSentinel["pending"] = "\0pending";
})(ContentsViewNameSentinel || (exports.ContentsViewNameSentinel = ContentsViewNameSentinel = {}));
exports.PENDING_CONTENTS_VIEW_NAME = ContentsViewNameSentinel.pending;
function placedSymbolLookupNames(symbolName) {
    const names = [symbolName];
    const viewSuffixMatch = /^(.*)\.(?:Normal|IEEE)$/.exec(symbolName);
    if (viewSuffixMatch) {
        names.push(viewSuffixMatch[1]);
    }
    return names;
}
function addSymbolPinLookupEntry(lookup, name, pins) {
    if (!name || pins.length === 0) {
        return;
    }
    lookup.set(name, pins);
    const viewSuffixMatch = /^(.*)\.(?:Normal|IEEE)$/.exec(name);
    if (viewSuffixMatch && !lookup.has(viewSuffixMatch[1])) {
        lookup.set(viewSuffixMatch[1], pins);
    }
}
function buildSymbolPinLookup(streams) {
    const lookup = new Map();
    for (const stream of streams) {
        const records = stream.decodedSymbolObjects ?? [];
        if (!records.some((record) => record.kind === 'symbol-pin')) {
            continue;
        }
        const parsed = stream.parsed;
        const fallbackName = parsed?.kind === 'package' ? parsed.packageName
            : parsed?.kind === 'symbol' ? parsed.symbolName
                : undefined;
        let currentGroupName = fallbackName;
        let currentPins = [];
        let sawViewHeader = false;
        const flushCurrentGroup = () => {
            addSymbolPinLookupEntry(lookup, currentGroupName, currentPins);
            currentPins = [];
        };
        for (const record of records) {
            if (record.kind === 'view-header') {
                flushCurrentGroup();
                sawViewHeader = true;
                currentGroupName = record.name;
                continue;
            }
            if (record.kind === 'symbol-pin') {
                currentPins.push(record);
            }
        }
        flushCurrentGroup();
        if (!sawViewHeader && fallbackName) {
            addSymbolPinLookupEntry(lookup, fallbackName, currentPins);
        }
    }
    return lookup;
}
function findPlacedSymbolPins(placedSymbol, symbolPinLookup) {
    for (const name of placedSymbolLookupNames(placedSymbol.symbolName)) {
        const pins = symbolPinLookup.get(name);
        if (pins) {
            return pins;
        }
    }
    return undefined;
}
function applySymbolPinMetadata(portInstance, symbolPin) {
    const nameCorroborated = portInstance.pinName === undefined || portInstance.pinName === symbolPin.name;
    portInstance.pinNameSource ??=
        portInstance.pinName === undefined ?
            OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PinNameSource.SymbolPin
            : OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PinNameSource.Record;
    portInstance.pinName ??= symbolPin.name;
    portInstance.startX = symbolPin.startX;
    portInstance.startY = symbolPin.startY;
    portInstance.hotptX = symbolPin.hotptX;
    portInstance.hotptY = symbolPin.hotptY;
    if (nameCorroborated) {
        portInstance.type ??= symbolPin.pinType;
    }
    return nameCorroborated;
}
function materializedPortFromExplicitPort(portInstance) {
    const nameSource = portInstance.pinNameSource ??
        (portInstance.pinName !== undefined ? OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PinNameSource.Record : undefined);
    return {
        name: portInstance.pinName ?? String(portInstance.pinIndex),
        ...(nameSource !== undefined ? { nameSource } : {}),
        position: portInstance.position,
        pinIndex: portInstance.pinIndex,
        ...(portInstance.startX !== undefined ? { startX: portInstance.startX } : {}),
        ...(portInstance.startY !== undefined ? { startY: portInstance.startY } : {}),
        ...(portInstance.hotptX !== undefined ? { hotptX: portInstance.hotptX } : {}),
        ...(portInstance.hotptY !== undefined ? { hotptY: portInstance.hotptY } : {}),
        x: portInstance.x,
        y: portInstance.y,
        ...(portInstance.wireDbId !== undefined ? { wireDbId: portInstance.wireDbId } : {}),
        isNoConnect: portInstance.isNoConnect,
        isBus: portInstance.isBus ?? portInstance.pinName?.includes('[') ?? false,
        ...(portInstance.type !== undefined ? { type: portInstance.type } : {}),
        ...(portInstance.visible !== undefined ? { visible: portInstance.visible } : {}),
        ...(portInstance.isPlacedInstPin !== undefined ? { isPlacedInstPin: portInstance.isPlacedInstPin } : {}),
        source: 'record',
        isExplicit: true,
    };
}
function materializedPortFromSymbolPin(symbolPin, position) {
    return {
        name: symbolPin.name,
        nameSource: OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PinNameSource.SymbolPin,
        position,
        pinIndex: position + 1,
        startX: symbolPin.startX,
        startY: symbolPin.startY,
        hotptX: symbolPin.hotptX,
        hotptY: symbolPin.hotptY,
        isNoConnect: false,
        isBus: symbolPin.name.includes('['),
        ...(symbolPin.pinType !== undefined ? { type: symbolPin.pinType } : {}),
        source: 'symbol-pin',
        isExplicit: false,
    };
}
function materializedPortFromDrawnPort(drawnPort) {
    return {
        name: drawnPort.name,
        startX: drawnPort.startX,
        startY: drawnPort.startY,
        hotptX: drawnPort.hotptX,
        hotptY: drawnPort.hotptY,
        isNoConnect: false,
        isBus: drawnPort.isBus,
        ...(drawnPort.type !== undefined ? { type: drawnPort.type } : {}),
        ...(drawnPort.visible !== undefined ? { visible: drawnPort.visible } : {}),
        source: 'drawn-port',
        isExplicit: true,
    };
}
function pkgNameFromGraphicName(graphicName) {
    return graphicName.replace(/\.(?:Normal|IEEE|Convert)$/i, '');
}
function baseDeviceDesignator(value) {
    return value.replace(/\d+$/u, '').trim() || value;
}
function isSelfReferencePlaceholder(value) {
    return /^&+$/.test(value.trim());
}
function userPropertiesFromDisplayProperties(displayProperties) {
    const properties = [];
    for (const property of displayProperties ?? []) {
        const value = property.displayedValue;
        if (value === undefined || isSelfReferencePlaceholder(value)) {
            continue;
        }
        properties.push({
            name: property.propertyName ?? `<unnamed property ${property.propertyCode}>`,
            value,
            source: 'display-property',
        });
    }
    return properties;
}
function libraryStringCatalogFromStreams(streams) {
    for (const stream of streams) {
        if (stream.parsed?.kind === 'library' && stream.parsed.stringCatalog) {
            return stream.parsed.stringCatalog;
        }
    }
    return undefined;
}
function isCatalogPropertyName(value) {
    return value !== undefined && value.length > 0 && value.length <= 64 && /^[A-Za-z]/.test(value);
}
function userPropertiesFromPropertyPairs(propertyPairs, stringCatalog) {
    const properties = [];
    for (const pair of propertyPairs) {
        const name = stringCatalog[pair.nameId];
        const value = stringCatalog[pair.valueId];
        if (!isCatalogPropertyName(name) || value === undefined || isSelfReferencePlaceholder(value)) {
            continue;
        }
        properties.push({ name, value, source: 'library-catalog' });
    }
    return properties;
}
function displayCatalogValue(placedSymbol, stringCatalog, diagnostics, streamPath) {
    const indices = [
        placedSymbol.referenceTail?.valueStringIndex,
        ...(placedSymbol.displayProperties ?? [])
            .filter((property) => {
            const pointerValue = property.libraryStringIndex === undefined ? undefined : stringCatalog[property.libraryStringIndex];
            return stringCatalog[property.propertyCode] === 'Value' || pointerValue !== property.displayedValue;
        })
            .map((property) => property.libraryStringIndex),
    ].filter((index) => index !== undefined && index > 0);
    const unresolved = indices.filter((index) => stringCatalog[index] === undefined);
    const values = new Set(indices.map((index) => stringCatalog[index]).filter((value) => value !== undefined));
    if (unresolved.length > 0 || values.size > 1) {
        placedSymbol.valueState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld;
        diagnostics.push({
            severity: 'warning',
            code: 'placement-value-withheld',
            streamPath,
            offset: placedSymbol.offset,
            message: `CFB placement value: tail/display catalogue pointers ${JSON.stringify(indices)} do not resolve and agree; unresolved=${JSON.stringify(unresolved)}, values=${JSON.stringify([...values])}`,
        });
        return undefined;
    }
    return placedSymbol.valueState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld ? undefined : [...values][0];
}
function enrichPageObjectsFromStringCatalog(streams, stringCatalog, diagnostics) {
    const referencedNames = new Set();
    const resolveDisplayProperty = (property) => {
        const name = stringCatalog[property.propertyCode];
        if (isCatalogPropertyName(name)) {
            property.propertyName = name;
            referencedNames.add(name);
        }
    };
    for (const stream of streams) {
        for (const pageObject of stream.decodedPageObjects ?? []) {
            if (pageObject.kind === 'wire' && pageObject.propertyPairs) {
                pageObject.userProperties = userPropertiesFromPropertyPairs(pageObject.propertyPairs, stringCatalog);
            }
            if (pageObject.kind === 'display-property') {
                resolveDisplayProperty(pageObject);
            }
            if (pageObject.kind === 'drawn-inst') {
                (pageObject.displayProperties ?? []).forEach(resolveDisplayProperty);
            }
            if (pageObject.kind === 'placed-symbol') {
                const placedName = stringCatalog[pageObject.recordType];
                if (placedName) {
                    pageObject.name = placedName;
                }
                (pageObject.displayProperties ?? []).forEach(resolveDisplayProperty);
                for (const instanceProperty of pageObject.instanceProperties ?? []) {
                    const name = stringCatalog[instanceProperty.propertyCode];
                    if (isCatalogPropertyName(name)) {
                        instanceProperty.name = name;
                        referencedNames.add(name);
                    }
                }
                const exactValue = displayCatalogValue(pageObject, stringCatalog, diagnostics, stream.path);
                if (pageObject.propertyPairs) {
                    const properties = userPropertiesFromPropertyPairs(pageObject.propertyPairs, stringCatalog);
                    pageObject.resolvedPropertyPairCount = properties.length;
                    properties.forEach((property) => referencedNames.add(property.name));
                    if (exactValue !== undefined && !properties.some((property) => property.name === 'Value')) {
                        properties.push({ name: 'Value', value: exactValue, source: 'library-catalog' });
                    }
                    if (properties.length > 0) {
                        pageObject.userProperties = properties;
                    }
                }
            }
        }
    }
    return referencedNames;
}
function applyLibraryPropertyNames(streams, referencedNames) {
    const names = Array.from(referencedNames).sort();
    for (const stream of streams) {
        if (stream.parsed?.kind === 'library') {
            stream.parsed.propertyNames = names;
        }
    }
}
function buildHierarchyLookup(streams, diagnostics = [], occurrenceTrust = (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)({ streams })) {
    const lookup = new Map();
    const candidates = new Map();
    for (const stream of streams) {
        const parsed = stream.parsed;
        if (parsed?.kind !== 'hierarchy') {
            continue;
        }
        for (const entry of parsed.entries) {
            if (entry.dbId !== undefined && entry.contentsViewName !== undefined) {
                const parentView = entry.parentViewName;
                const reason = parentView === undefined ? 'counted parent is unknown' : (((0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyAdoptionVeto)(occurrenceTrust, stream.path, entry.contentsViewName, entry.dbId) ??
                    (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyAdoptionVeto)(occurrenceTrust, stream.path, parentView, entry.dbId, entry.parentBranchOffset, entry.recordId)));
                if (reason !== undefined || parentView === undefined) {
                    diagnostics.push({
                        severity: 'warning',
                        code: 'hierarchy-adoption-withheld',
                        streamPath: stream.path,
                        offset: entry.offset,
                        message: `CFB hierarchy [hierarchy-adoption-withheld] drawn-instance dbId=${entry.dbId}, parentOffset=${entry.parentBranchOffset}: ${reason}`,
                    });
                    continue;
                }
                const key = (0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyPlacementKey)(parentView, entry.dbId);
                const claims = candidates.get(key) ?? [];
                claims.push(entry);
                candidates.set(key, claims);
            }
        }
    }
    for (const [key, rows] of candidates) {
        if (new Set(rows.map((row) => row.contentsViewName)).size === 1) {
            lookup.set(key, rows[0]);
        }
        else {
            diagnostics.push({
                severity: 'warning',
                code: 'hierarchy-adoption-withheld',
                message: `CFB hierarchy [hierarchy-adoption-withheld] drawn-instance ${key}: conflicting child views`,
            });
        }
    }
    return lookup;
}
exports.__testing = { buildHierarchyLookup };
function enrichPlacedSymbolMetadata(placedSymbol, diagnostics, streamPath) {
    const catalogUserProperties = (placedSymbol.userProperties ?? []).filter((property) => property.source === 'library-catalog');
    const userProperties = catalogUserProperties.length > 0 ?
        catalogUserProperties
        : userPropertiesFromDisplayProperties(placedSymbol.displayProperties);
    if (placedSymbol.valueState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementValueState.Withheld) {
        for (const properties of [userProperties, placedSymbol.userProperties ?? []]) {
            for (let index = properties.length - 1; index >= 0; index--) {
                if (properties[index].name === 'Value') {
                    properties.splice(index, 1);
                }
            }
        }
        placedSymbol.partValue = undefined;
    }
    if (userProperties.length > 0) {
        placedSymbol.userProperties = userProperties;
    }
    const displayReferences = (placedSymbol.displayProperties ?? [])
        .filter((property) => property.propertyName === 'Part Reference')
        .map((property) => property.displayedValue)
        .filter((value) => value !== undefined && value.length > 0);
    if (placedSymbol.referenceTail !== undefined) {
        const reference = placedSymbol.referenceTail.reference;
        const candidates = [...displayReferences, placedSymbol.reference].filter((value) => value !== undefined && value.length > 0);
        if (candidates.some((candidate) => candidate !== reference)) {
            placedSymbol.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld;
            diagnostics.push({
                severity: 'warning',
                code: 'placement-reference-tail-withheld',
                streamPath,
                offset: placedSymbol.referenceTail.offset,
                message: `CFB placement reference: tail ${JSON.stringify(reference)} disagrees with decoded reference evidence ${JSON.stringify(candidates)}`,
            });
        }
        else if (placedSymbol.referenceState !== OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld) {
            placedSymbol.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Resolved;
            placedSymbol.reference = reference.length === 0 || reference.endsWith('?') ? undefined : reference;
        }
    }
    else {
        if (displayReferences.some((reference) => reference !== displayReferences[0])) {
            placedSymbol.referenceState = OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld;
            diagnostics.push({
                severity: 'warning',
                code: 'placement-reference-tail-withheld',
                streamPath,
                offset: placedSymbol.offset,
                message: `CFB placement reference: explicit Part Reference displays disagree: ${JSON.stringify(displayReferences)}`,
            });
        }
        else {
            placedSymbol.reference ??= displayReferences[0];
        }
    }
    if (placedSymbol.referenceState === OrcadDsnCfbTypes_1.OrcadDsnCfbTypes.PlacementReferenceState.Withheld) {
        placedSymbol.reference = undefined;
    }
    const pkgName = placedSymbol.role === 'part-inst' ? pkgNameFromGraphicName(placedSymbol.graphicName) : undefined;
    const pcbLib = userProperties.find((property) => property.name === 'PCB Footprint')?.value;
    const partValue = userProperties.find((property) => property.name === 'Value' && property.value !== placedSymbol.reference)?.value;
    if (partValue) {
        placedSymbol.partValue = partValue;
    }
    placedSymbol.resolvedMetadata = {
        kind: placedSymbol.role,
        name: placedSymbol.reference ? `INS${placedSymbol.dbId}` : undefined,
        ...(placedSymbol.reference ? { reference: placedSymbol.reference } : {}),
        ...(placedSymbol.partValue ? { partValue: placedSymbol.partValue } : {}),
        ...(pkgName ? { pkgName } : {}),
        contentsLibName: '',
        contentsViewName: placedSymbol.role === 'part-inst' ? exports.PENDING_CONTENTS_VIEW_NAME : undefined,
        contentsViewType: placedSymbol.role === 'part-inst' ? 5 : undefined,
        isPrimitiveProp: placedSymbol.role === 'part-inst' ? 2 : undefined,
        powerPinsVisible: false,
        mirror: (placedSymbol.flags & 0x04) !== 0 ? 1 : 0,
        rotation: placedSymbol.flags & 0x03,
        ...(pcbLib ? { pcbLib } : {}),
        ...(userProperties.length > 0 ? { userProperties } : {}),
    };
}
function enrichDrawnInstMetadata(drawnInst, hierarchyLookup, viewName) {
    const hierarchy = hierarchyLookup.get((0, OrcadDsnCfbOccurrenceOwnership_1.hierarchyPlacementKey)(viewName, drawnInst.dbId));
    if (hierarchy?.contentsViewName) {
        drawnInst.contentsViewName = hierarchy.contentsViewName;
        drawnInst.partValue = hierarchy.contentsViewName;
    }
    drawnInst.contentsLibName = '';
    drawnInst.contentsViewType = 1;
    drawnInst.isPrimitiveProp = 2;
    if (drawnInst.reference && !drawnInst.deviceDesignator) {
        drawnInst.deviceDesignator = drawnInst.reference;
    }
    if (drawnInst.recordFrame !== undefined && drawnInst.recordedName !== undefined) {
        drawnInst.name = drawnInst.recordedName;
    }
    else if (drawnInst.deviceDesignator && !drawnInst.name) {
        drawnInst.name = baseDeviceDesignator(drawnInst.deviceDesignator);
    }
    drawnInst.resolvedMetadata = {
        kind: 'drawn-inst',
        ...(drawnInst.name ? { name: drawnInst.name } : {}),
        ...(drawnInst.reference ? { reference: drawnInst.reference } : {}),
        ...(drawnInst.partValue ? { partValue: drawnInst.partValue } : {}),
        ...(drawnInst.deviceDesignator ? { deviceDesignator: drawnInst.deviceDesignator } : {}),
        contentsLibName: drawnInst.contentsLibName,
        contentsViewName: drawnInst.contentsViewName,
        contentsViewType: drawnInst.contentsViewType,
        isPrimitiveProp: drawnInst.isPrimitiveProp,
        mirror: drawnInst.mirror,
        rotation: drawnInst.rotation,
    };
    drawnInst.materializedPortInstances = (drawnInst.portInstances ?? []).map(materializedPortFromDrawnPort);
}
function enrichPagePortInstancesFromSymbolPins(streams, diagnostics, occurrenceTrust) {
    const symbolPinLookup = buildSymbolPinLookup(streams);
    const hierarchyLookup = buildHierarchyLookup(streams, diagnostics, occurrenceTrust);
    const uncorroboratedSymbolNames = new Map();
    for (const stream of streams) {
        if (!stream.decodedPageObjects) {
            continue;
        }
        if (stream.decodedPageObjectsTruncated) {
            diagnostics.push({
                severity: 'warning',
                code: 'decodedPageObjectsTruncated',
                message: `page stream truncated: kept ${stream.decodedPageObjects.length} of ${Object.values(stream.decodedPageObjectCounts ?? {}).reduce((sum, count) => sum + count, 0)} decoded object(s); port-instance enrichment skipped`,
                streamPath: stream.path,
            });
            continue;
        }
        for (const pageObject of stream.decodedPageObjects) {
            if (pageObject.kind === 'drawn-inst') {
                enrichDrawnInstMetadata(pageObject, hierarchyLookup, stream.parsed?.kind === 'page' ? stream.parsed.viewName : '');
                continue;
            }
            if (pageObject.kind !== 'placed-symbol') {
                continue;
            }
            enrichPlacedSymbolMetadata(pageObject, diagnostics, stream.path);
            const symbolPins = findPlacedSymbolPins(pageObject, symbolPinLookup);
            if (!symbolPins) {
                pageObject.materializedPortInstances = (pageObject.portInstances ?? []).map(materializedPortFromExplicitPort);
                continue;
            }
            pageObject.symbolPins = symbolPins;
            const existingPortInstancesByPosition = new Map();
            for (const portInstance of pageObject.portInstances ?? []) {
                existingPortInstancesByPosition.set(portInstance.position, portInstance);
            }
            for (let position = 0; position < symbolPins.length; position++) {
                const symbolPin = symbolPins[position];
                const existingPortInstance = existingPortInstancesByPosition.get(position);
                const metadataMatches = existingPortInstance ? applySymbolPinMetadata(existingPortInstance, symbolPin) : true;
                if (!metadataMatches) {
                    const symbols = uncorroboratedSymbolNames.get(stream.path) ?? new Set();
                    symbols.add(pageObject.symbolName);
                    uncorroboratedSymbolNames.set(stream.path, symbols);
                }
            }
            const positions = new Set(symbolPins.map((_, position) => position));
            for (const portInstance of pageObject.portInstances ?? []) {
                positions.add(portInstance.position);
            }
            pageObject.materializedPortInstances = Array.from(positions)
                .sort((left, right) => left - right)
                .map((position) => {
                const existingPortInstance = existingPortInstancesByPosition.get(position);
                if (existingPortInstance) {
                    return materializedPortFromExplicitPort(existingPortInstance);
                }
                return materializedPortFromSymbolPin(symbolPins[position], position);
            });
        }
    }
    for (const [streamPath, symbolNames] of uncorroboratedSymbolNames) {
        const names = [...symbolNames].sort();
        diagnostics.push({
            severity: 'info',
            code: 'placed-pin-definition-name-mismatch',
            message: `page port records disagree with the symbol definition's pin names on ${names.length} symbol(s) (${names.join(', ')}); those ports take no pin type from the definition`,
            streamPath,
        });
    }
}
function materializeOrCadDsnCfbDocument(entries, streams, hierarchyOccurrenceTrust = (0, OrcadDsnCfbOccurrenceOwnership_1.readHierarchyOccurrenceTrust)({ streams })) {
    const diagnostics = [];
    const stringCatalog = libraryStringCatalogFromStreams(streams);
    if (stringCatalog) {
        const referencedNames = enrichPageObjectsFromStringCatalog(streams, stringCatalog, diagnostics);
        applyLibraryPropertyNames(streams, referencedNames);
    }
    enrichPagePortInstancesFromSymbolPins(streams, diagnostics, hierarchyOccurrenceTrust);
    const entriesWithoutContent = entries.map(({ content, ...entry }) => entry);
    const storages = entriesWithoutContent.filter((entry) => entry.type === 1 || entry.type === 5);
    const schematicViews = streams
        .map((stream) => stream.parsed)
        .filter((parsed) => parsed?.kind === 'schematic');
    const paths = entries.map((entry) => OrcadDsnCfbStreamUtils_1.OrcadDsnCfbStreamUtils.stripRoot(entry.path));
    return {
        entries: entriesWithoutContent,
        streams,
        hierarchyOccurrenceTrust,
        storages,
        schematicViews,
        summary: {
            entryCount: entries.length,
            streamCount: streams.length,
            storageCount: storages.length,
            viewCount: entries.filter((entry) => /^Root Entry\/Views\/[^/]+\/$/.test(entry.path)).length,
            pageStreamCount: paths.filter((path) => /^Views\/[^/]+\/Pages\/[^/]+$/.test(path)).length,
            packageStreamCount: paths.filter((path) => /^Packages\/[^/]+$/.test(path)).length,
            symbolStreamCount: paths.filter((path) => /^Symbols\/[^/]+$/.test(path)).length,
        },
        ...(diagnostics.length > 0 ? { diagnostics } : {}),
    };
}
//# sourceMappingURL=OrcadDsnCfbMaterializer.js.map